/**
 * How a token-refresh attempt ended, and the one rule that decides whether a session may be ended.
 *
 * Deliberately dependency-free: the refresh manager itself pulls in the logger and request-context,
 * which makes it awkward to exercise in isolation, and this is the part whose correctness actually
 * matters. Getting it wrong logs people out of a working session.
 */

/**
 * - `success`     — a fresh token is in place (or one recent enough to still be valid).
 * - `retry-later` — nothing is wrong with the session: we are inside the failure backoff, too soon
 *                   after a previous attempt, or the refresh call itself never got an answer
 *                   (network drop, 5xx). Surface a retryable error and leave auth alone.
 * - `rejected`    — the refresh function reported the credentials are no longer good.
 */
export type RefreshOutcome = 'success' | 'retry-later' | 'rejected';

/**
 * Classify a completed attempt.
 *
 * `threw` is the distinction the old boolean return could not carry: a refresh that throws says
 * nothing about whether the credentials are still valid, so it must never read as a rejection.
 */
export function classifyRefreshAttempt({ ok, threw }: { ok: boolean; threw: boolean }): RefreshOutcome {
  if (ok) return 'success';
  return threw ? 'retry-later' : 'rejected';
}

/**
 * May this outcome end the user's session?
 *
 * Only an explicit rejection. This is the rule that was missing: every non-success was treated as
 * an expiry, so a throttled refresh or a 502 on the refresh endpoint signed people out.
 */
export function shouldExpireSession(outcome: RefreshOutcome): boolean {
  return outcome === 'rejected';
}
